// The emoji picker. One component, two jobs. Lifted from the Andon app.
//
//   REACTING     tapped from beside a message. What comes back goes on that
//                message as a chip, and the person who wrote it is told.
//   WRITING      tapped from the composer's bar. What comes back is inserted
//                into the draft at the caret, like any other character.
//
// The same sheet for both, because it is the same question — which face — and
// two pickers would be two palettes to keep in step. What differs is only what
// the caller does with the answer.
//
// NO SEARCH BOX. The whole set is sixty characters across six labelled groups
// and fits inside one thumb's reach; a search box here would be a keyboard, on
// a phone, with gloves on, to find something already on screen. See
// lib/emoji.ts for why the list is short in the first place.

import { Sheet } from '@/components/ui/Sheet';
import { useT } from '@/i18n/useT';
import { EMOJI_GROUPS } from '@/lib/emoji';

export function EmojiPicker({
  open,
  onClose,
  onPick,
  title,
  chosen = [],
}: {
  open: boolean;
  onClose: () => void;
  onPick: (emoji: string) => void;
  /** What the sheet is called, since it is reached from two different places. */
  title: string;
  /** Lit and ticked — the ones this person has already put on this message. */
  chosen?: readonly string[];
}) {
  const t = useT();
  return (
    <Sheet open={open} onClose={onClose} variant="tall" title={title}>
      {EMOJI_GROUPS.map((group) => (
        <section key={group.label} style={{ marginBottom: 14 }}>
          <div style={{ font: "700 9px 'Space Grotesk',sans-serif", letterSpacing: '.09em', color: 'var(--sub)', margin: '0 2px 7px', textTransform: 'uppercase' }}>
            {t[group.label]}
          </div>
          <div
            style={{
              display: 'grid',
              // 44px is the smallest target a gloved thumb hits reliably, and
              // `auto-fill` rather than a fixed count so a tablet's wider sheet
              // uses the space instead of stretching eight of them.
              gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
              gap: 4,
            }}
          >
            {group.emoji.map((emoji) => {
              const on = chosen.includes(emoji);
              return (
                <button
                  key={emoji}
                  type="button"
                  className="press"
                  aria-pressed={on}
                  // The emoji IS the label. A `<button>` with only an emoji in
                  // it announces as the character's own Unicode name, which is
                  // what a screen reader user wants here — "thumbs up".
                  onClick={() => onPick(emoji)}
                  style={{
                    height: 44,
                    borderRadius: 12,
                    background: on ? 'var(--soft)' : 'var(--surface)',
                    border: `1px solid ${on ? 'var(--accent)' : 'var(--line)'}`,
                    fontSize: 22,
                    lineHeight: 1,
                    display: 'grid',
                    placeItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </Sheet>
  );
}
